import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SubmitHandler } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import TournamentDetailsPreview from './TournamentDetailsPreview';
import TournamentTickerConfigSection from './components/TournamentTickerConfigSection';
import { useDispatch, useSelector } from 'react-redux';
import { saveTournamentDraft } from '../../../redux/slice/createTournamentSlice';
import { useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { fetchLeagueById } from '../../myLeagues/api/myLeaguesApi';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import {
  BALL_TYPES,
  type BallType,
  FIELD_LIMITS,
  getBallTypeLabel,
  type ICreateTournament,
  type ICreateTournamentUI,
  MATCH_TYPES,
  schema,
  TOURNAMENT_CATEGORIES,
} from './types/createtournament';
import type { TickerRowState } from './tournamentsDetails/matches/tickerAutomationConstants';
import { ProfileDatePicker } from '../../profileOnboarding/components/ProfileDatePicker';

export interface TournamentBannerState {
  base64: string;
  fileType: string;
  fileName: string;
  previewUrl: string;
}

export interface TournamentDetailsWizardPayload {
  data: ICreateTournamentUI;
  banner: TournamentBannerState | null;
  tickerRows: TickerRowState[];
}

type TournamentDetailsProps = {
  /** Legacy: embedded preview + internal create call. */
  onTournamentCreatedSuccess?: (tournamentId?: string) => void;
  onTournamentCreatedBackToHome?: () => void;
  /** Wizard mode: when set, "NEXT" calls this instead of the embedded preview. */
  onNext?: (payload: TournamentDetailsWizardPayload) => void;
  initialValues?: Partial<ICreateTournamentUI>;
  initialBanner?: TournamentBannerState | null;
  initialTickerRows?: TickerRowState[];
};

const TournamentDetails: React.FC<TournamentDetailsProps> = ({
  onTournamentCreatedSuccess,
  onTournamentCreatedBackToHome,
  onNext,
  initialValues,
  initialBanner,
  initialTickerRows,
}) => {
  const wizardMode = !!onNext;
  const dispatch = useDispatch();
  const location = useLocation();
  const [tournamentPreview, setTournamentPreview] = useState(false);
  const isEditMode = location?.state?.pathname?.("/create-tournament");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ICreateTournamentUI, any, ICreateTournamentUI>({
    resolver: yupResolver<ICreateTournamentUI, any, ICreateTournamentUI>(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      leagueId: null,
      bannerUri: null,
      canEditTournament: null,
      isRosterLocked: false,
      canEditScoreCard: false,
      isImpactPlayerAllowed: false,
      bonusPointsEnabled: false,
      isStrictModeEnabled: false,
      isTickerAutomationEnabled: false,
      tournamentName: '',
      description: '',
      organizerName: '',
      organizerPhoneNumber: '',
      organizerPhoneNumberCountryCode: '',
      organizerEmailId: '',
      startDate: '',
      endDate: '',
      ...initialValues,
    },
  });

  const watchedValues = useWatch({ control }) as ICreateTournamentUI;
  const canPreview = useMemo(() => schema.isValidSync(watchedValues), [watchedValues]);

  useEffect(() => {
    setValue('leagueId', null, { shouldDirty: false });
    setValue('bannerUri', null, { shouldDirty: false });
  }, [setValue]);

  const matchTypeWatch = watch('matchType');
  const ballTypeWatch = watch('ballType');
  const categoryWatch = watch('tournamentCategory');
  const tickerEnabledWatch = watch('isTickerAutomationEnabled');
  const tournamentNameW = watch('tournamentName') ?? '';
  const organizerNameW = watch('organizerName') ?? '';
  const organizerEmailW = watch('organizerEmailId') ?? '';
  const descriptionW = watch('description') ?? '';

  const counter = (value: string, max: number) =>
    value.length >= max - 10 ? (
      <span className="ct-char-counter">
        {value.length}/{max}
      </span>
    ) : null;

  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const { data: leagueDetails } = useQuery({
    queryKey: [QUERY_KEY.getLeagueDetails, selectedLeagueId],
    queryFn: () => fetchLeagueById(selectedLeagueId as string),
    enabled: !!selectedLeagueId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!leagueDetails) return;
    const current = watch();
    if (!current.organizerName && leagueDetails.organizerName) {
      setValue('organizerName', leagueDetails.organizerName, { shouldDirty: false });
    }
    if (!current.organizerEmailId && leagueDetails.organizerEmailId) {
      setValue('organizerEmailId', leagueDetails.organizerEmailId, { shouldDirty: false });
    }
    if (!current.organizerPhoneNumber && leagueDetails.organizerPhoneNumber) {
      const cc =
        leagueDetails.countryCode != null
          ? String(leagueDetails.countryCode).replace(/^\+/, '')
          : leagueDetails.organizer?.countryCode != null
            ? String(leagueDetails.organizer.countryCode).replace(/^\+/, '')
            : '91';
      const rawPhone = String(leagueDetails.organizerPhoneNumber).replace(/\D/g, '');
      const combined = rawPhone.startsWith(cc) ? rawPhone : `${cc}${rawPhone}`;
      setValue('organizerPhoneNumber', combined, { shouldDirty: false });
      setValue('organizerPhoneNumberCountryCode', cc, { shouldDirty: false });
    }
  }, [leagueDetails, setValue, watch]);

  const [banner, setBanner] = useState<TournamentBannerState | null>(initialBanner ?? null);
  const [tickerRows, setTickerRows] = useState<TickerRowState[]>(initialTickerRows ?? []);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;
      const ext =
        (file.type || '').split('/')[1]?.toLowerCase().split(';')[0] ||
        file.name.split('.').pop()?.toLowerCase() ||
        'png';
      setBanner({ base64, fileType: ext, fileName: file.name, previewUrl: result });
    };
    reader.readAsDataURL(file);
  };

  const fc = (hasErr?: boolean) => `form-control ${hasErr ? 'field-error' : ''}`;

  const onSubmitForm: SubmitHandler<ICreateTournamentUI> = (data) => {
    const payloadUI: ICreateTournamentUI & {
      bannerBase64?: string | null;
      bannerFileType?: string | null;
      bannerFileName?: string | null;
    } = {
      ...data,
      leagueId: data.leagueId ?? null,
      bannerUri: data.bannerUri ?? null,
      bannerBase64: banner?.base64 ?? null,
      bannerFileType: banner?.fileType ?? null,
      bannerFileName: banner?.fileName ?? null,
    };

    if (wizardMode) {
      onNext?.({ data: payloadUI, banner, tickerRows });
      return;
    }

    dispatch(saveTournamentDraft(payloadUI as unknown as ICreateTournament));
    setTournamentPreview(true);
  };

  const draft = useSelector((s: any) => s.createTournament.draft);

  useEffect(() => {
    if (isEditMode) {
      if (draft) reset(draft);
    }
    const d = draft as
      | (ICreateTournamentUI & {
          bannerBase64?: string | null;
          bannerFileType?: string | null;
          bannerFileName?: string | null;
        })
      | null;
    if (d?.bannerBase64 && d?.bannerFileType) {
      setBanner({
        base64: d.bannerBase64,
        fileType: d.bannerFileType,
        fileName: d.bannerFileName ?? 'banner',
        previewUrl: `data:${d.bannerFileType};base64,${d.bannerBase64}`,
      });
    }
  }, [draft, reset, isEditMode]);

  return (
    <div>
      <div className="common-stepper-details-wrap">
        {!tournamentPreview ? (
          <form className="ct-form" onSubmit={handleSubmit(onSubmitForm)}>
            <h2 className="sec-title">Tournament Details</h2>

            <div className="form-control-wrap">
              <div className="form-group">
                <label>
                  Tournament Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Tournament Name"
                  maxLength={FIELD_LIMITS.tournamentName}
                  className={fc(!!errors.tournamentName)}
                  {...register('tournamentName')}
                />
                {counter(tournamentNameW, FIELD_LIMITS.tournamentName)}
                {errors.tournamentName && <p className="err-msg">{errors.tournamentName.message}</p>}
              </div>

              <div className="form-group">
                <label>
                  Mobile Number <span className="req">*</span>
                </label>
                <div className="phone">
                  <Controller
                    control={control}
                    name="organizerPhoneNumber"
                    render={({ field }) => (
                      <PhoneInput
                        country="in"
                        value={field.value ?? ''}
                        countryCodeEditable={false}
                        onChange={(value, country: { dialCode?: string }) => {
                          field.onChange(value);
                          setValue('organizerPhoneNumberCountryCode', country?.dialCode || '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                        inputClass={`${fc(!!errors.organizerPhoneNumber)} phone-input-field`}
                        containerClass="phone-input-container"
                        placeholder="Enter contact number"
                        enableSearch
                      />
                    )}
                  />
                  <input type="hidden" {...register('organizerPhoneNumberCountryCode')} />
                </div>
                {(errors.organizerPhoneNumber || errors.organizerPhoneNumberCountryCode) && (
                  <p className="err-msg">
                    {errors.organizerPhoneNumber?.message || errors.organizerPhoneNumberCountryCode?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="form-control-wrap">
              <div className="form-group">
                <label htmlFor="tournament-start-date">
                  Tournaments Date <span className="req">*</span>
                </label>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <ProfileDatePicker
                      id="tournament-start-date"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Start Date"
                      hasError={!!errors.startDate}
                      disablePast
                    />
                  )}
                />
                {errors.startDate && <p className="err-msg">{errors.startDate.message}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="tournament-end-date">
                  End Date <span className="req">*</span>
                </label>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <ProfileDatePicker
                      id="tournament-end-date"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="End Date"
                      hasError={!!errors.endDate}
                      disablePast
                    />
                  )}
                />
                {errors.endDate && <p className="err-msg">{errors.endDate.message}</p>}
              </div>
            </div>

            <div className="form-control-wrap">
              <div className="form-group">
                <label>
                  Organizer Email <span className="req">*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter Email id"
                  maxLength={FIELD_LIMITS.organizerEmail}
                  className={fc(!!errors.organizerEmailId)}
                  {...register('organizerEmailId')}
                />
                {counter(organizerEmailW, FIELD_LIMITS.organizerEmail)}
                {errors.organizerEmailId && <p className="err-msg">{errors.organizerEmailId.message}</p>}
              </div>
              <div className="form-group">
                <label>
                  Organizer Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Organizer Name"
                  maxLength={FIELD_LIMITS.organizerName}
                  className={fc(!!errors.organizerName)}
                  {...register('organizerName')}
                />
                {counter(organizerNameW, FIELD_LIMITS.organizerName)}
                {errors.organizerName && <p className="err-msg">{errors.organizerName.message}</p>}
              </div>
            </div>

            <hr className="soft-sep" />

            <h2 className="sec-title">Scoring Rules & Settings</h2>

            <div className="score-rule-wrap">
              <div className="over-bolwer">
                <label>
                  Overs per Bowler <span className="req">*</span>
                </label>
                <input
                  type="number"
                  placeholder="Enter overs"
                  className={fc(!!errors.maxOversPerBowler)}
                  {...register('maxOversPerBowler', { valueAsNumber: true })}
                />
                {errors.maxOversPerBowler && <p className="err-msg">{errors.maxOversPerBowler.message}</p>}
              </div>

              <div className="points-data-wrap">
                <div className="points-data">
                  <label>
                    Points for Win <span className="req">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Points"
                    className={fc(!!errors.pointForWin)}
                    {...register('pointForWin', { valueAsNumber: true })}
                  />
                  {errors.pointForWin && <p className="err-msg">{errors.pointForWin.message}</p>}
                </div>
                <div className="points-data">
                  <label>
                    Points for Tie <span className="req">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Points"
                    className={fc(!!errors.pointForTie)}
                    {...register('pointForTie', { valueAsNumber: true })}
                  />
                  {errors.pointForTie && <p className="err-msg">{errors.pointForTie.message}</p>}
                </div>
                <div className="points-data">
                  <label>
                    Points for Abandoned <span className="req">*</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Points"
                    className={fc(!!errors.pointForAbandoned)}
                    {...register('pointForAbandoned', { valueAsNumber: true })}
                  />
                  {errors.pointForAbandoned && <p className="err-msg">{errors.pointForAbandoned.message}</p>}
                </div>
              </div>
            </div>

            <div className="match-type-wrapper">
              <div className="match-type">
                <label>
                  Match Format <span className="req">*</span>
                </label>
                <div className="chip-group">
                  {MATCH_TYPES.map((mt) => (
                    <button
                      type="button"
                      key={mt}
                      className={`chip ${matchTypeWatch === mt ? 'selected' : ''}`}
                      onClick={() => setValue('matchType', mt, { shouldValidate: true, shouldDirty: true })}
                    >
                      {mt === 'THE_HUNDRED' ? 'THE HUNDRED' : mt.replaceAll('_', ' ')}
                    </button>
                  ))}
                </div>
                {errors.matchType && <p className="err-msg">{errors.matchType.message as string}</p>}
              </div>

              <div className="ball-type">
                <label>
                  Ball <span className="req">*</span>
                </label>
                <div className="chip-group">
                  {BALL_TYPES.map((bt) => (
                    <button
                      type="button"
                      key={bt}
                      className={`chip ${ballTypeWatch === bt ? 'selected' : ''}`}
                      onClick={() => setValue('ballType', bt, { shouldValidate: true, shouldDirty: true })}
                    >
                      {getBallTypeLabel(bt as BallType)}
                    </button>
                  ))}
                </div>
                {errors.ballType && <p className="err-msg">{errors.ballType.message as string}</p>}
              </div>
            </div>

            {matchTypeWatch === 'OTHER_LIMITED_OVER' && (
              <div className="other-limited-wrap">
                <div className="over-wrap">
                  <label>
                    No. Of Overs <span className="req">*</span>
                  </label>
                  <input
                    className={fc(!!errors.maxOvers)}
                    placeholder="Enter Overs"
                    {...register('maxOvers', { valueAsNumber: true })}
                  />
                  {errors.maxOvers && <p className="err-msg">{errors.maxOvers.message as string}</p>}
                </div>
                <div className="wicket-wrap">
                  <label>
                    No. Of Wickets <span className="req">*</span>
                  </label>
                  <input
                    className={fc(!!errors.maxWickets)}
                    placeholder="Enter wickets"
                    {...register('maxWickets', { valueAsNumber: true })}
                  />
                  {errors.maxWickets && <p className="err-msg">{errors.maxWickets.message as string}</p>}
                </div>
              </div>
            )}

            <div className="tournament-category-wrapper">
              <div className="category-wrap">
                <label>
                  Tournament Category <span className="req">*</span>
                </label>
                <div className="chip-group">
                  {TOURNAMENT_CATEGORIES.map((cat) => (
                    <button
                      type="button"
                      key={cat}
                      className={`chip ${categoryWatch === cat ? 'selected' : ''}`}
                      onClick={() =>
                        setValue('tournamentCategory', cat, { shouldValidate: true, shouldDirty: true })
                      }
                    >
                      {cat.charAt(0) + cat.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                {errors.tournamentCategory && (
                  <p className="err-msg">{errors.tournamentCategory.message as string}</p>
                )}
              </div>
            </div>

            <input type="hidden" {...register('leagueId', { setValueAs: () => null })} />
            <input type="hidden" {...register('bannerUri', { setValueAs: () => null })} />

            <div className="toggles">
              <label className="toggle">
                <span className="label">Lock Roster</span>
                <input type="checkbox" {...register('isRosterLocked')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Allow Other Players to Edit Score</span>
                <input type="checkbox" {...register('canEditScoreCard')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Allow Impact Player</span>
                <input type="checkbox" {...register('isImpactPlayerAllowed')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Enable Bonus Points</span>
                <input type="checkbox" {...register('bonusPointsEnabled')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Strict Mode</span>
                <input type="checkbox" {...register('isStrictModeEnabled')} />
                <span className="slider"></span>
              </label>
              <label className="toggle">
                <span className="label">Enable Ticker Automation</span>
                <input type="checkbox" {...register('isTickerAutomationEnabled')} />
                <span className="slider"></span>
              </label>
            </div>

            {tickerEnabledWatch && (
              <TournamentTickerConfigSection
                initial={tickerRows}
                onChange={setTickerRows}
              />
            )}

            <div className="form-group-textarea">
              <label>
                Tournament Description <span className="req">*</span>
              </label>
              <textarea
                rows={5}
                placeholder="Enter description"
                maxLength={FIELD_LIMITS.description}
                className={`textarea ${errors.description ? 'field-error' : ''}`}
                {...register('description')}
              />
              {counter(descriptionW, FIELD_LIMITS.description)}
              {errors.description && <p className="err-msg">{errors.description.message}</p>}
            </div>

            <div className="form-group-banner">
              <label>Banner Image</label>
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleBannerChange}
              />
              {banner?.previewUrl ? (
                <div className="banner-dropzone has-image">
                  <img
                    src={banner.previewUrl}
                    alt="Banner preview"
                    className="banner-dropzone-img"
                  />
                  <div className="banner-dropzone-overlay">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => bannerInputRef.current?.click()}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="btn btn-text"
                      onClick={() => {
                        setBanner(null);
                        if (bannerInputRef.current) bannerInputRef.current.value = '';
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="banner-dropzone empty"
                  onClick={() => bannerInputRef.current?.click()}
                >
                  <span className="banner-dropzone-icon" aria-hidden>+</span>
                  <span className="banner-dropzone-title">Click to upload banner</span>
                  <span className="banner-dropzone-hint">PNG, JPG up to 5MB</span>
                </button>
              )}
            </div>

            <div className="actions">
              <button type="submit" className="btn btn-primary" disabled={!canPreview}>
                {wizardMode ? 'NEXT' : 'PREVIEW'}
              </button>
            </div>
          </form>
        ) : (
          <TournamentDetailsPreview
            onEdit={() => setTournamentPreview(false)}
            onTournamentCreatedSuccess={onTournamentCreatedSuccess}
            onTournamentCreatedBackToHome={onTournamentCreatedBackToHome}
          />
        )}
      </div>
    </div>
  );
};

export default TournamentDetails;
